import React from 'react';
import { Table, Button, Tag, Tooltip, Typography, Space } from 'antd';
import { EyeOutlined, SettingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ItemMasterData, ItemMasterDataPaginationParams } from '../../../types/item-master-data';
import {
  STATUS_COLORS,
  HAS_EDIT_COLORS,
  TABLE_COLUMN_WIDTHS,
  TIME_FORMAT,
  NUMBER_FORMAT_CONFIG,
  PAGE_SIZE_OPTIONS,
} from '../../../utils/itemMasterDataConstants';
import dayjs from 'dayjs';

const { Text } = Typography;

interface ItemMasterDataTableProps {
  data: ItemMasterData[];
  pagination: {
    current: number;
    pageSize: number;
    total: number;
  };
  loading: boolean;
  onPageChange: (page: number, pageSize: number) => void;
  onSortChange: (field: string, order: 'asc' | 'desc') => void;
  onRefresh: () => void;
  onView: (itemCode: string) => void;
  onConfigDisplay: (itemCode: string) => void;
  selectedRowKeys: string[];
  setSelectedRowKeys: (keys: string[]) => void;
}

const ItemMasterDataTable: React.FC<ItemMasterDataTableProps> = ({
  data,
  pagination,
  loading,
  onPageChange,
  onSortChange,
  onRefresh,
  onView,
  onConfigDisplay,
  selectedRowKeys,
  setSelectedRowKeys,
}) => {
  const { t } = useTranslation();

  // 为PAGE_SIZE_OPTIONS添加翻译
  const translatedPageSizeOptions = PAGE_SIZE_OPTIONS.map(option => ({
    ...option,
    label: t(option.label)
  }));

  // 获取商品类型翻译的辅助函数
  const getItemTypeTranslation = (itemType: string) => {
    if (!itemType) return itemType;
    
    // 根据实际的itemType值映射到翻译键值
    const typeMap: { [key: string]: string } = {
      'location+facing': 'location+facing',
      'location only': 'location only',
      'storage only': 'storage only'
    };
    
    const mappedType = typeMap[itemType] || itemType;
    const translationKey = `itemMasterData.itemType.${mappedType}`;
    const translation = t(translationKey);
    
    // 如果翻译键值不存在，返回原始值
    return translation === translationKey ? itemType : translation;
  };

  // Handle row selection change
  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys as string[]);
  };

  // Row selection config
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record: ItemMasterData) => ({
      disabled: false,
    }),
  };

  // Format time
  const formatTime = (timestamp: number) => {
    return timestamp ? dayjs(timestamp).format(TIME_FORMAT.dateTime) : '';
  };

  // Format price
  const formatPrice = (price: number) => {
    const config = NUMBER_FORMAT_CONFIG.price;
    return price?.toFixed(config.precision);
  };

  // Table columns
  const columns = [
    {
      title: t('itemMasterData.table.ownerCode'),
      dataIndex: 'ownerCode',
      key: 'ownerCode',
      // width: TABLE_COLUMN_WIDTHS.ownerCode,
      fixed: 'left' as const,
      width: 100,
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: t('itemMasterData.table.itemCode'),
      dataIndex: 'code',
      key: 'code',
      // width: TABLE_COLUMN_WIDTHS.itemCode,
      fixed: 'left' as const,
      // sorter: true,
      width: 120,
      render: (text: string) => (
        <Text copyable={{ text }} code>
          {text}
        </Text>
      ),
    },
    {
      title: t('itemMasterData.table.itemName'),
      dataIndex: 'name',
      key: 'name',
      width: 150,
      // width: TABLE_COLUMN_WIDTHS.itemName,
      // sorter: true,
      // ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip title={text}>
          <Text>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: t('itemMasterData.table.englishName'),
      dataIndex: 'englishName',
      key: 'englishName',
      width: 120,
      // width: TABLE_COLUMN_WIDTHS.englishName,
      // ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip title={text}>
          <Text>{text}</Text>
        </Tooltip>
      ),
    },
    // {
    //   title: t('itemMasterData.table.shortName'),
    //   dataIndex: 'abbr',
    //   key: 'abbr',
    //   // width: TABLE_COLUMN_WIDTHS.shortName,
    //   // ellipsis: { showTitle: false },
    //   render: (text: string) => (
    //     <Tooltip title={text}>
    //       <Text>{text}</Text>
    //     </Tooltip>
    //   ),
    // },
    // {
    //   title: t('itemMasterData.table.stockUnit'),
    //   dataIndex: 'stockUnit',
    //   key: 'stockUnit',
    //   // width: TABLE_COLUMN_WIDTHS.stockUnit,
    //   render: (text: string) => <Text code>{text}</Text>,
    // },
    // {
    //   title: t('itemMasterData.table.itemType'),
    //   dataIndex: 'itemType',
    //   key: 'itemType',
    //   width: TABLE_COLUMN_WIDTHS.itemType,
    //   sorter: true,
    //   render: (type: string) => getItemTypeTranslation(type),
    // },
    {
      title: t('itemMasterData.table.status'),
      dataIndex: 'status',
      key: 'status',
      // width: TABLE_COLUMN_WIDTHS.status,
      // sorter: true,
      width: 100,
      render: (status: string) => (
        <Tag color={STATUS_COLORS[status as keyof typeof STATUS_COLORS]}>
          {status}
        </Tag>
      ),
    },
    {
      title: t('itemMasterData.table.hasEdit'),
      dataIndex: 'hasEdit',
      key: 'hasEdit',
      width: 120,
      // sorter: true,
      render: (hasEdit: boolean) => (
        <Tag color={HAS_EDIT_COLORS[hasEdit.toString() as keyof typeof HAS_EDIT_COLORS]}>
          {t(`itemMasterData.hasEdit.${hasEdit}`)}
        </Tag>
      ),
    },
    {
      title: t('itemMasterData.displayConfig.allowHole'),
      dataIndex: 'allowHole',
      key: 'allowHole',
      width: 120,
      render: (data: boolean) => renderYesOrNo(data),
    },
    {
      title: t('itemMasterData.displayConfig.allowStack'),
      dataIndex: 'allowStack',
      key: 'allowStack',
      width: 120,
      render: (data: boolean) => renderYesOrNo(data),
    },
    {
      title: t('itemMasterData.displayConfig.nestingSettings'),
      dataIndex: 'allowNest',
      key: 'allowNest',
      width: 120,
      render: (data: boolean) => renderYesOrNo(data),
    },
    {
      title: t('itemMasterData.displayConfig.oritation.label'),
      dataIndex: 'oritation',
      key: 'oritation',
      width: 120,
    },
    {
      title: t('itemMasterData.table.brandName'),
      dataIndex: 'brandName',
      key: 'brandName',
      width: 100,
      // width: TABLE_COLUMN_WIDTHS.brandName,
      ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip title={text}>
          <Text>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: t('itemMasterData.table.category1Name'),
      dataIndex: 'category1Name',
      key: 'category1Name',
      width: 150,
      ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip title={text}>
          <Text>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: t('itemMasterData.table.category2Name'),
      dataIndex: 'category2Name',
      key: 'category2Name',
      width: 150,
      ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip title={text}>
          <Text>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: t('itemMasterData.table.category4Name'),
      dataIndex: 'category4Name',
      key: 'category4Name',
      width: 150,
    },
    {
      title: t('itemMasterData.table.category5Name'),
      dataIndex: 'category5Name',
      key: 'category5Name',
      width: 150,
    },
    {
      title: t('itemMasterData.table.price'),
      dataIndex: 'retailPrice',
      key: 'retailPrice',
      width: 120,
      // width: TABLE_COLUMN_WIDTHS.price,
      // sorter: true,
      render: (retailPrice: number) => formatPrice(retailPrice),
    },
    {
      title: t('itemMasterData.table.displayType'),
      dataIndex: 'displayType',
      key: 'displayType',
      width: 120,
      // width: TABLE_COLUMN_WIDTHS.displayType,
      // render: (type: string) => t(`itemMasterData.displayType.${type}`) || type,
    },
    {
      title: t('itemMasterData.table.strategicFlag'),
      dataIndex: 'strategicFlag',
      key: 'strategicFlag',
      width: 120,
      // width: TABLE_COLUMN_WIDTHS.strategicFlag,
      align: 'center' as const,
      render: (flag: boolean) => (
        <Tag color={flag ? 'green' : 'default'}>
          {flag ? t('itemMasterData.detail.yes') : t('itemMasterData.detail.no')}
        </Tag>
      ),
    },
    {
      title: t('itemMasterData.table.firstReceivedTime'),
      dataIndex: 'firstReceivedTime',
      key: 'firstReceivedTime',
      width: 180,
      // width: TABLE_COLUMN_WIDTHS.firstReceivedTime,
      // sorter: (a, b) => (a.firstReceivedTime ?? 0) - (b.firstReceivedTime ?? 0),
      render: (timestamp: number) => formatTime(timestamp),
    },
    {
      title: t('itemMasterData.table.gmtCreate'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      width: 180,
      // width: TABLE_COLUMN_WIDTHS.gmtCreate,
      // sorter: true,
      render: (timestamp: number) => formatTime(timestamp),
    },
    {
      title: t('itemMasterData.table.gmtModified'),
      dataIndex: 'gmtModified',
      key: 'gmtModified',
      width: 180,
      // width: TABLE_COLUMN_WIDTHS.gmtModified,
      // sorter: true,
      render: (timestamp: number) => formatTime(timestamp),
    },
    {
      title: t('itemMasterData.table.actions'),
      key: 'actions',
      fixed: 'right' as const,
      width: 120,
      render: (_: any, record: ItemMasterData) => (
        <Space size="small">
          <Tooltip title={t('itemMasterData.viewDetails')}>
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              onClick={() => onView(record.code)}
            />
          </Tooltip>
          <Tooltip title={t('itemMasterData.configDisplayParams')}>
            <Button 
              type="text" 
              icon={<SettingOutlined />} 
              onClick={() => onConfigDisplay(record.code)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const renderYesOrNo = (data) => {
      // Use translation keys instead of hard-coded labels
      let labelKey: string;
      if (data) {
        labelKey = "common.yes";
      } else {
        labelKey = "common.no";
      }
  
      return (
        <Tag>
          {t(labelKey)}
        </Tag>
      );
    };

  // Handle table change (sorting, pagination)
  const handleTableChange = (paginationConfig: any, _: any, sorter: any) => {
    const { current, pageSize } = paginationConfig;
    const { field, order } = sorter;

    // 对于firstReceivedTime字段，只在前端排序，不调用后端
    if (field === 'firstReceivedTime') {
      // 只处理分页变化，不触发排序回调
      if (current !== pagination.current || pageSize !== pagination.pageSize) {
        onPageChange(current, pageSize);
      }
      return;
    }

    onPageChange(current, pageSize);
    if (field && order) {
      onSortChange(field, order === 'ascend' ? 'asc' : 'desc');
    }
  };

  return (
    <Table
      rowSelection={rowSelection}
      columns={columns}
      dataSource={data}
      rowKey="code"
      loading={loading}
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total) => t('itemMasterData.totalRecords', { total }),
        pageSizeOptions: translatedPageSizeOptions.map(option => option.value.toString()),
      }}
      onChange={handleTableChange}
      scroll={{ x: 1800, y: 500 }}
      size="middle"
    />
  );
};

export default ItemMasterDataTable;